// Pure matching functions for mapping persona demographic descriptions
// to filter checkbox values. Persona demographics are free-text like
// "Female-dominated (71%)" or "$50,000-$150,000", and these need fuzzy
// matching against checkbox values like "Female" or "50k-75k".

export function matchesPersonaValue(checkboxValue, personaValue, filterKey) {
  switch (filterKey) {
    case 'gender':
      return matchesGender(checkboxValue, personaValue)
    case 'home_ownership':
      return matchesHomeOwnership(checkboxValue, personaValue)
    case 'presence_of_children':
      return matchesChildren(checkboxValue, personaValue)
    case 'marital_status':
      return matchesMaritalStatus(checkboxValue, personaValue)
    default:
      return matchesRange(checkboxValue, personaValue)
  }
}

// "Female-dominated (71%)" -> match "Female" but not "Male"
function matchesGender(checkboxValue, personaValue) {
  return new RegExp(`\\b${checkboxValue}\\b`, 'i').test(personaValue)
}

// "Homeowner (79% own)" -> match "Own"; "Mix of Own/Rent" -> match both
function matchesHomeOwnership(checkboxValue, personaValue) {
  const pv = personaValue.toLowerCase()
  const cv = checkboxValue.toLowerCase()
  if (cv === 'own') return pv.includes('own') || pv.includes('homeowner')
  if (cv === 'rent') return pv.includes('rent')
  return false
}

// "Empty nesters (62% no children)" -> "No"; "Mixed presence" -> both
function matchesChildren(checkboxValue, personaValue) {
  const pv = personaValue.toLowerCase()
  const cv = checkboxValue.toLowerCase()
  if (pv.includes('mixed')) return true
  if (cv === 'no') return pv.includes('no children') || pv.includes('empty nest')
  if (cv === 'yes') return pv.includes('have children')
  return false
}

// "Mixed Single/Married" -> match all; "Skews Single" -> match "Single"
function matchesMaritalStatus(checkboxValue, personaValue) {
  const pv = personaValue.toLowerCase()
  if (pv.includes('mix') || pv.includes('both')) return true
  return pv.includes(checkboxValue.toLowerCase())
}

// Numeric range overlap for age ("55-75" vs "55-64") and income ("$50,000-$150,000" vs "50k-75k")
function matchesRange(checkboxValue, personaValue) {
  const pvRange = extractRange(personaValue)
  const cvRange = extractRange(checkboxValue)
  if (!pvRange || !cvRange) return false

  // "65+" style: checkbox has open-ended upper bound
  if (checkboxValue.includes('+')) {
    return pvRange.max >= cvRange.min
  }

  // Standard overlap: ranges intersect if each starts before the other ends
  return pvRange.min <= cvRange.max && cvRange.min <= pvRange.max
}

// Extracts a numeric {min, max} range from strings like "$50,000-$150,000", "50k-75k", "55-64", "65+"
function extractRange(str) {
  const normalized = str.replace(/[$,]/g, '').replace(/(\d+)k/gi, (_, n) => String(n * 1000))
  const nums = normalized.match(/\d+/g)?.map(Number)
  if (!nums || nums.length === 0) return null
  return { min: nums[0], max: nums[nums.length - 1] }
}
